import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { map, switchMap } from "rxjs/operators";
import { AuthService, AuthType } from "src/app/shared/services/auth.service";
import { IStemexeModule } from "../data-providers/interfaces/interfaces";
import { DataProvider, PagedData, PagedDataItem } from "../data-providers/models/models";
import { PageableResponse, Product, SearchProduct } from "./interfaces";
import { ProductStatus } from "./enums";
import { TechnadoptTokenResolverGuard } from "../shared/guards/technadopt-token-resolver-guard.service";

@Injectable({
  providedIn: "root"
})
export class TechnadoptDataProviderService implements IStemexeModule {
  type: AuthType = AuthType.Technadopt;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private tokenResolver: TechnadoptTokenResolverGuard
  ) {
  }

  initialize(): void {
  }

getDataProviders(providerType: string): Observable<DataProvider[]> {
    let technadoptDataProviders: DataProvider[] = [];
    if (providerType == 'ActivitySource') {
      return this.authService.getAuthAddonByType(AuthType.Technadopt).pipe(
        switchMap(addon => {
          let tDataProvider: DataProvider = new DataProvider();
          tDataProvider.title = 'Technadopt';
          tDataProvider.id = '7';
          tDataProvider.system = addon?.BaseUrl;
          tDataProvider.tenantId = '0'; //addon?.OrganziationId?.toString() || '';
          tDataProvider.getData = (token, isAll ) => {
            return this._getProviderData(tDataProvider, token, isAll);
          };
          technadoptDataProviders.push(tDataProvider);
          return of(technadoptDataProviders);
        })
      );
    }
    return of([]);
  }

  private _getProviderData(provider: DataProvider, nextPageToken?: any, isAll?: any): Observable<PagedData> {
    if (provider.id === '7') {
      return this.authService.getAuthAddonByType(AuthType.Technadopt).pipe(
        switchMap(addon => {
          if (!addon) {
            return of(new PagedData());
          }
          const userAccessListIds = this.tokenResolver.getTechnadoptAccessList(addon);
          const organizationId = userAccessListIds.map(obj => Object.values(obj)[0]).join(',');
          const search: SearchProduct = {
            start: nextPageToken?.start || 0,
            limit: nextPageToken?.limit || 50,
            searchTerm: nextPageToken?.searchTerm || '',
            activeOnly: isAll ? false : true,
            organziationId: organizationId,
          };

          const url = `${addon.BaseUrl}/api/exceeders/topics`;
          let params: HttpParams = new HttpParams({
            fromObject: {
              start: search.start?.toString() || '0',
              limit: search.limit?.toString() || '50',
              search: search.searchTerm || '',
              filterTags: search.tags?.join(',') || '',
              filterCategories: search.categories?.join(',') || '',
              accessListIds: search.organziationId || '',
              activeOnly: search.activeOnly ? 'true' : 'false',
            },
          });

          // Remove empty params
          params.keys().forEach((key) => {
            const val = params.get(key);
            if (!val) {
              params = params.delete(key);
            }
          });

          const options = {
            headers: new HttpHeaders({
              Authorization: `Bearer ${addon.AccessToken}`,
            }),
            params,
          };

          return this.http
            .get<{ Paging: PageableResponse[]; Topics: Product[] }>(url, options)
            .pipe(
              map((result: { Paging: PageableResponse[]; Topics: Product[] }) => {
                let pagedData = new PagedData();

                // Set next page token if more data is available
                const paging = result.Paging?.[0];
                if (paging && paging.TotalCount && (search.start + search.limit) < paging.TotalCount) {
                  pagedData.nextPageToken = {
                    start: search.start + search.limit,
                    limit: search.limit,
                    searchTerm: search.searchTerm
                  };
                }

                pagedData.items = result.Topics.map((topic: Product) => {
                  // Set status based on IsActive and Locked
                  if (topic.IsActive) {
                    topic.Status = ProductStatus.Visible;
                  } else if (topic.Locked) {
                    topic.Status = ProductStatus.Lock;
                  } else {
                    topic.Status = ProductStatus.Hide;
                  }

                  let item = new PagedDataItem();
                  item.id = topic.TopicId || '';
                  item.title = topic.TopicName || '';
                  item.extraData = {
                    description: topic.TopicDescription,
                    imageUrl: topic.ImageUrl,
                    tags: topic.TopicTags,
                    category: topic.TopicCategory,
                    owner: topic.TopicOwner,
                    isActive: topic.IsActive,
                    locked: topic.Locked,
                    status: topic.Status,
                    canEdit: topic.CanEdit,
                    coverImageUrl: topic.CoverImageURL,
                    defaultVideoUrl: topic.DefaultVideoURL,
                    slug: topic.Slug
                  };
                  item.url = `/pages/library/product/${topic.TopicId}`;
                  item.getData = (nextToken) => this._getTopicDetails(provider, item, nextToken);
                  return item;
                });

                return pagedData;
              })
            );
        })
      );
    } else {
      return of(new PagedData());
    }
  }

  private _getTopicDetails(provider: DataProvider, pagedDataItem: PagedDataItem, nextPageToken?: any): Observable<PagedData> {
    // Return the extra data as a single item for topic details
    let pagedData = new PagedData();
    let detailItem = new PagedDataItem();
    detailItem.id = pagedDataItem.id;
    detailItem.title = pagedDataItem.title;
    detailItem.extraData = pagedDataItem.extraData;
    detailItem.url = pagedDataItem.url;
    pagedData.items = [detailItem];
    return of(pagedData);
  }

  openUrl(url: string, context: any): any {
    if (url.startsWith('/pages/library/')) {
      // Handle library navigation
    }
  }
}
